import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, fmtMoney } from "@/lib/api";
import { SpeakUpLogo } from "@/components/Layout";
import {
    Mic,
    Send,
    ArrowRight,
    Shield,
    Globe2,
    Clock,
    Sparkles,
    Languages,
    Trophy,
    Check,
    ChevronDown,
    MessageSquareText,
    FileText,
    Lock,
    HeartHandshake,
    Phone,
} from "lucide-react";

const publicApi = axios.create({ baseURL: API });

/**
 * SpeakUp — public landing page.
 * Light theme, brand red reserved for action, Bricolage/Hanken/Noto fonts (loaded in index.html).
 * Sections: nav · hero · proof strip · how-it-works · features · live institutions · privacy/trust ·
 * wall-of-wins teaser · pricing · FAQ · final CTA · footer.
 */
export default function Landing() {
    const [stats, setStats] = useState(null);
    const [entities, setEntities] = useState([]);

    useEffect(() => {
        publicApi.get(`/wins/stats`).then((r) => setStats(r.data)).catch(() => {});
        publicApi.get(`/entities`).then((r) => setEntities(r.data.items || [])).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-white text-[var(--speakup-text)] selection:bg-[rgba(242,59,54,0.15)]">
            <LandingNav />

            <Hero />

            <ProofStrip stats={stats} liveCount={entities.length} />

            <HowItWorks />

            <FeaturesGrid />

            <LiveInstitutions entities={entities} />

            <TrustPrivacy />

            <WinsTeaser stats={stats} />

            <Pricing />

            <Faq />

            <FinalCta />

            <LandingFooter />
        </div>
    );
}

/* ---------------- Nav ---------------- */
function LandingNav() {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 4);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return (
        <header
            className={`sticky top-0 z-40 transition-all ${
                scrolled
                    ? "bg-white/90 backdrop-blur border-b border-[var(--speakup-border)]"
                    : "bg-white/0"
            }`}
            data-testid="landing-nav"
        >
            <div className="max-w-6xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5" data-testid="landing-nav-logo">
                    <SpeakUpLogo size={30} />
                    <span
                        className="display text-[20px] tracking-tight"
                        style={{ color: "var(--speakup-text-strong)" }}
                    >
                        SpeakUp
                    </span>
                </Link>
                <nav className="hidden md:flex items-center gap-7 text-[14px]">
                    <a href="#how" className="text-[var(--speakup-text-muted)] hover:text-black">
                        How it works
                    </a>
                    <a href="#features" className="text-[var(--speakup-text-muted)] hover:text-black">
                        Features
                    </a>
                    <a href="#institutions" className="text-[var(--speakup-text-muted)] hover:text-black">
                        Supported banks
                    </a>
                    <Link to="/wins" className="text-[var(--speakup-text-muted)] hover:text-black" data-testid="landing-nav-wins">
                        Wall of Wins
                    </Link>
                    <a href="#faq" className="text-[var(--speakup-text-muted)] hover:text-black">
                        FAQ
                    </a>
                </nav>
                <div className="flex items-center gap-2.5">
                    <Link
                        to="/login"
                        className="hidden sm:inline-flex h-10 px-4 items-center text-[14px] font-semibold text-[var(--speakup-text-strong)] hover:underline"
                        data-testid="landing-nav-login"
                    >
                        Log in
                    </Link>
                    <Link
                        to="/login"
                        data-testid="landing-nav-cta"
                        className="h-10 px-4 inline-flex items-center gap-1.5 rounded-[10px] text-white text-[14px] font-semibold"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        Raise a complaint <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        </header>
    );
}

/* ---------------- Hero ---------------- */
function Hero() {
    return (
        <section className="relative overflow-hidden" data-testid="landing-hero">
            {/* subtle red wash bottom-right */}
            <div
                aria-hidden
                className="absolute -right-40 -top-20 w-[480px] h-[480px] rounded-full opacity-[0.08] blur-3xl pointer-events-none"
                style={{ background: "var(--speakup-red)" }}
            />
            <div className="max-w-6xl mx-auto px-5 md:px-8 pt-12 md:pt-24 pb-16 md:pb-24 grid md:grid-cols-12 gap-12 items-center">
                <div className="md:col-span-7 speakup-fade-up">
                    <div className="inline-flex items-center gap-2 chip chip-action mb-5" data-testid="hero-pill">
                        <Sparkles size={12} /> Built for Indian banking consumers
                    </div>
                    <h1
                        className="display leading-[1.02] text-[44px] sm:text-[58px] md:text-[68px] lg:text-[76px] tracking-[-0.02em]"
                        style={{ color: "var(--speakup-text-strong)" }}
                    >
                        Got billed wrong?
                        <br />
                        Bank ignoring you?
                        <br />
                        <span style={{ color: "var(--speakup-red)" }}>Speak it.</span> We'll take it up.
                    </h1>
                    <p className="mt-6 text-[17px] md:text-[18px] text-[var(--speakup-text-muted)] max-w-xl leading-relaxed">
                        Tap the mic, tell us in <strong className="text-[var(--speakup-text-strong)]">your language</strong> what went wrong with your bank,
                        NBFC or card. SpeakUp drafts the formal complaint, tells you exactly whom to send it to, and
                        walks it up the RBI ladder all the way to the Ombudsman.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Link
                            to="/login"
                            data-testid="hero-primary-cta"
                            className="h-14 px-6 inline-flex items-center gap-2 rounded-[14px] text-white text-[16px] font-semibold shadow-[0_8px_24px_-8px_rgba(242,59,54,0.55)]"
                            style={{ background: "var(--speakup-red)" }}
                        >
                            <Mic size={18} /> Raise a complaint — free
                        </Link>
                        <Link
                            to="/wins"
                            data-testid="hero-secondary-cta"
                            className="h-14 px-5 inline-flex items-center gap-2 rounded-[14px] text-[15px] font-semibold border border-[var(--speakup-border)] hover:border-[var(--speakup-text-strong)] transition-colors"
                        >
                            <Trophy size={16} /> See real wins
                        </Link>
                    </div>
                    <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-[var(--speakup-text-muted)]">
                        <span className="inline-flex items-center gap-1.5">
                            <Check size={14} className="text-[var(--speakup-resolved)]" /> Free, always
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Check size={14} className="text-[var(--speakup-resolved)]" /> No legal advice — only help
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Check size={14} className="text-[var(--speakup-resolved)]" /> RB-IOS 2021 compliant
                        </span>
                    </div>
                </div>

                {/* Phone mock */}
                <div className="md:col-span-5 flex justify-center md:justify-end speakup-fade-up" style={{ animationDelay: "120ms" }}>
                    <PhoneMock />
                </div>
            </div>
        </section>
    );
}

function PhoneMock() {
    return (
        <div
            className="relative"
            style={{
                width: 290,
                maxWidth: "100%",
                aspectRatio: "290 / 580",
            }}
        >
            <div
                className="absolute inset-0 rounded-[40px] shadow-[0_30px_60px_-20px_rgba(16,24,40,0.25),0_18px_36px_-18px_rgba(242,59,54,0.20)] border border-[var(--speakup-border)] bg-white overflow-hidden"
                aria-hidden
            >
                {/* notch */}
                <div className="h-7 flex items-center justify-center">
                    <span className="w-24 h-5 rounded-full bg-[var(--speakup-text-strong)]" />
                </div>
                <div className="px-4 pt-2 pb-4 h-[calc(100%-28px)] flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                        <SpeakUpLogo size={22} />
                        <span className="display text-[16px]" style={{ color: "var(--speakup-text-strong)" }}>
                            SpeakUp
                        </span>
                    </div>

                    {/* complaint card */}
                    <div className="speakup-card p-3.5">
                        <div className="text-[10px] uppercase tracking-wider text-[var(--speakup-text-muted)]">
                            HDFC Bank · Customer Care
                        </div>
                        <div className="display text-[13.5px] leading-snug mt-1.5">
                            Wrong ₹4,200 charge on card ending 4471 reversed
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1.5 chip chip-resolved">
                            <Check size={11} /> Resolved
                        </div>
                    </div>

                    {/* timeline */}
                    <div className="relative mt-4 pl-5">
                        <span className="absolute left-[7px] top-1.5 bottom-1.5 w-px bg-[var(--speakup-border)]" />
                        {[
                            ["L1 · Customer Care", "Sent · 12 Jan", "resolved"],
                            ["L2 · Nodal Officer", "Resolved · 19 Jan", "resolved"],
                            ["L3 · Principal Nodal", "Not needed", "muted"],
                            ["L4 · RBI Ombudsman", "Not needed", "muted"],
                        ].map(([t, sub, kind], i) => (
                            <div key={t} className="relative pb-3">
                                <span
                                    className="absolute -left-[14px] top-1 h-3 w-3 rounded-full ring-4 ring-white"
                                    style={{
                                        background:
                                            kind === "resolved" ? "var(--speakup-resolved)" : "var(--speakup-border)",
                                    }}
                                />
                                <div className="text-[11.5px] font-semibold text-[var(--speakup-text-strong)]">{t}</div>
                                <div className="text-[10.5px] text-[var(--speakup-text-muted)] tabular">{sub}</div>
                            </div>
                        ))}
                    </div>

                    {/* mic dock */}
                    <div className="mt-auto pt-3 flex flex-col items-center">
                        <button
                            type="button"
                            tabIndex={-1}
                            aria-hidden
                            className="h-14 w-14 rounded-full flex items-center justify-center text-white speakup-mic-pulse"
                            style={{ background: "var(--speakup-gradient)" }}
                        >
                            <Mic size={22} />
                        </button>
                        <div className="text-[10px] text-[var(--speakup-text-muted)] mt-2">Tap to speak</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ---------------- Proof strip ---------------- */
function ProofStrip({ stats, liveCount }) {
    const items = [
        { k: "Languages supported", v: "Hindi · Tamil · English · +" },
        { k: "Institutions live", v: liveCount ? `${liveCount} · adding weekly` : "Adding weekly" },
        { k: "Cost to you", v: "₹0 · forever" },
        { k: "Wins published", v: stats ? `${stats.total_wins}` : "—" },
    ];
    return (
        <section className="border-y border-[var(--speakup-border)] bg-[var(--speakup-surface-muted)]/60">
            <div className="max-w-6xl mx-auto px-5 md:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                {items.map((it, i) => (
                    <div key={it.k} className="text-[13px]" data-testid={`proof-${i}`}>
                        <div className="text-[10.5px] uppercase tracking-[0.08em] text-[var(--speakup-text-muted)]">
                            {it.k}
                        </div>
                        <div className="display text-[16px] mt-0.5 tabular">{it.v}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ---------------- How it works ---------------- */
function HowItWorks() {
    const steps = [
        {
            icon: <Mic size={22} />,
            title: "Speak in your language",
            body: "Tap the big red mic. Hindi, English, Tamil, and more. We capture the facts — bank, amount, dates, your card or account reference.",
            kind: "action",
        },
        {
            icon: <FileText size={22} />,
            title: "We draft the formal complaint",
            body: "Polite, factual, under 230 words. Includes the exact relief you want and the number where the bank can reach you.",
            kind: "progress",
        },
        {
            icon: <Send size={22} />,
            title: "You send it — from your email",
            body: "We open Gmail or your default mail app with everything prefilled. A hidden BCC comes back to us so we know it's sent.",
            kind: "waiting",
        },
        {
            icon: <Sparkles size={22} />,
            title: "We escalate up the RBI ladder",
            body: "If the bank stalls, we draft for the Nodal Officer, then the Principal Nodal Officer. At Day 30 or rejection, the RBI Ombudsman packet unlocks.",
            kind: "regulator",
        },
    ];
    return (
        <section id="how" className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28" data-testid="landing-how">
            <SectionLabel>How it works</SectionLabel>
            <h2 className="display text-[34px] md:text-[48px] mt-3 max-w-2xl leading-[1.05]">
                A voice. A draft. A send. A receipt of justice.
            </h2>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {steps.map((s, i) => (
                    <div key={s.title} className="relative speakup-card p-5 md:p-6" data-testid={`how-step-${i + 1}`}>
                        <div
                            className={`h-12 w-12 rounded-full flex items-center justify-center chip-${
                                { action: "action", progress: "progress", waiting: "waiting", regulator: "regulator" }[s.kind]
                            }`}
                            style={{ borderRadius: 999 }}
                        >
                            {s.icon}
                        </div>
                        <div className="text-[11px] uppercase tracking-wider text-[var(--speakup-text-muted)] mt-5 tabular">
                            Step {i + 1}
                        </div>
                        <div className="display text-[18px] mt-1 leading-snug">{s.title}</div>
                        <p className="text-[14px] text-[var(--speakup-text-muted)] mt-2 leading-relaxed">{s.body}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ---------------- Features ---------------- */
function FeaturesGrid() {
    const items = [
        {
            icon: <Languages size={18} />,
            t: "Voice-first, in your language",
            d: "Powered by Sarvam Saaras v3 — speak in Hindi, Tamil, English, Telugu, Bengali, Marathi, Gujarati and more. We auto-detect.",
        },
        {
            icon: <FileText size={18} />,
            t: "Smart, factual drafting",
            d: "Claude-powered. No embellishment, no legal threats, no invented facts — just a polite, formal complaint with the right structure.",
        },
        {
            icon: <Clock size={18} />,
            t: "Two-clock escalation engine",
            d: "Per-level SLA + an absolute 30-day clock from your first send. When the bank stalls, we draft for the next officer up.",
        },
        {
            icon: <Lock size={18} />,
            t: "You always send the email",
            d: "SpeakUp never emails your bank itself. You send from your own Gmail with one tap — and stay in full control.",
        },
        {
            icon: <Shield size={18} />,
            t: "RBI Ombudsman co-pilot",
            d: "When eligible, we assemble the packet, walk you through cms.rbi.org.in step-by-step, and prepare the CRPC email fallback.",
        },
        {
            icon: <HeartHandshake size={18} />,
            t: "Wall of Wins",
            d: "Real, anonymised outcomes from people like you — opt-in only, no names or numbers. Proof that the ladder works.",
        },
    ];
    return (
        <section id="features" className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28" data-testid="landing-features">
            <SectionLabel>What you get</SectionLabel>
            <h2 className="display text-[34px] md:text-[48px] mt-3 max-w-3xl leading-[1.05]">
                Everything the bank's complaint cell wishes you didn't know.
            </h2>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((it, i) => (
                    <div key={it.t} className="speakup-card p-5 md:p-6" data-testid={`feature-card-${i}`}>
                        <div className="h-9 w-9 rounded-full bg-[var(--speakup-surface-muted)] border border-[var(--speakup-border)] flex items-center justify-center text-[var(--speakup-text-strong)]">
                            {it.icon}
                        </div>
                        <div className="display text-[18px] mt-4 leading-snug">{it.t}</div>
                        <p className="text-[14px] text-[var(--speakup-text-muted)] mt-2 leading-relaxed">{it.d}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ---------------- Live institutions ---------------- */
function LiveInstitutions({ entities }) {
    return (
        <section
            id="institutions"
            className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28"
            data-testid="landing-institutions"
        >
            <SectionLabel>Live right now</SectionLabel>
            <h2 className="display text-[34px] md:text-[48px] mt-3 max-w-3xl leading-[1.05]">
                Pre-mapped escalation ladders for these institutions.
            </h2>
            <p className="text-[var(--speakup-text-muted)] mt-3 max-w-2xl">
                Each one is wired all the way through Customer Care → Nodal Officer → Principal Nodal Officer, with the exact email
                addresses on file. Don't see yours? Sign up and tap "Request we add it" — we'll add it.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="landing-institution-chips">
                {entities.length === 0 && (
                    <div className="text-[14px] text-[var(--speakup-text-muted)]">Loading…</div>
                )}
                {entities.map((e) => (
                    <div
                        key={e.id}
                        className="speakup-card px-4 py-3 flex items-center gap-3"
                        data-testid={`landing-inst-${e.id}`}
                    >
                        <span
                            className="h-9 w-9 rounded-full flex items-center justify-center text-white text-[12px] font-bold"
                            style={{ background: "var(--speakup-gradient)" }}
                        >
                            {e.name
                                .split(/\s+/)
                                .slice(0, 2)
                                .map((w) => w[0])
                                .join("")
                                .toUpperCase()}
                        </span>
                        <div>
                            <div className="font-semibold text-[14px] text-[var(--speakup-text-strong)]">{e.name}</div>
                            <div className="text-[11px] uppercase tracking-wider text-[var(--speakup-text-muted)]">
                                {e.type} · L1 / L2 / L3 wired
                            </div>
                        </div>
                    </div>
                ))}
                <Link
                    to="/login"
                    data-testid="landing-request-entity-cta"
                    className="speakup-card px-4 py-3 flex items-center gap-2 border-dashed text-[var(--speakup-red)] font-semibold"
                    style={{ borderStyle: "dashed" }}
                >
                    + Request we add yours <ArrowRight size={14} />
                </Link>
            </div>
        </section>
    );
}

/* ---------------- Trust / Privacy ---------------- */
function TrustPrivacy() {
    const lines = [
        {
            icon: <Lock size={18} />,
            t: "Voice clips auto-expire in 30 days.",
            d: "We store the working transcript, not your voice forever.",
        },
        {
            icon: <Shield size={18} />,
            t: "DPDP-compliant consent up-front.",
            d: "Versioned notice. Clear purpose. Withdraw anytime.",
        },
        {
            icon: <MessageSquareText size={18} />,
            t: "We never send email to your bank.",
            d: "You always send from your own inbox — full control, zero impersonation risk.",
        },
        {
            icon: <Globe2 size={18} />,
            t: "Delete everything in one tap.",
            d: "Account → Delete my data. Complaints, voice clips, your account — gone.",
        },
    ];
    return (
        <section className="pt-20 md:pt-28">
            <div className="max-w-6xl mx-auto px-5 md:px-8">
                <SectionLabel>Built for trust, not engagement</SectionLabel>
                <h2 className="display text-[34px] md:text-[48px] mt-3 max-w-3xl leading-[1.05]">
                    A grievance tool is no place to mine your data.
                </h2>
            </div>
            <div className="max-w-6xl mx-auto px-5 md:px-8 mt-10 grid md:grid-cols-2 gap-5" data-testid="landing-trust">
                {lines.map((l) => (
                    <div
                        key={l.t}
                        className="rounded-[14px] p-5 md:p-6 flex items-start gap-4"
                        style={{
                            background: "rgba(22,163,74,0.05)",
                            border: "1px solid rgba(22,163,74,0.20)",
                        }}
                    >
                        <div className="h-10 w-10 rounded-full bg-white border border-[var(--speakup-border)] flex items-center justify-center text-[var(--speakup-resolved)]">
                            {l.icon}
                        </div>
                        <div>
                            <div className="font-semibold text-[var(--speakup-text-strong)]">{l.t}</div>
                            <div className="text-[14px] text-[var(--speakup-text-muted)] mt-1">{l.d}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ---------------- Wins teaser ---------------- */
function WinsTeaser({ stats }) {
    return (
        <section className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28">
            <div
                className="rounded-[18px] p-7 md:p-10 grid md:grid-cols-12 gap-6 items-center"
                style={{
                    background: "linear-gradient(180deg, #18181B 0%, #232328 100%)",
                    color: "#fff",
                }}
                data-testid="landing-wins-teaser"
            >
                <div className="md:col-span-7">
                    <div className="inline-flex items-center gap-2 chip chip-regulator">
                        <Trophy size={12} /> Wall of Wins
                    </div>
                    <h2 className="display text-[28px] md:text-[40px] mt-3 leading-[1.08]" style={{ color: "#fff" }}>
                        Real outcomes. Anonymised. Opt-in.
                    </h2>
                    <p className="text-white/70 mt-3 max-w-xl text-[15px]">
                        Every win on the wall is a user who chose to share — institution, issue, days to resolve. No names.
                        No phone numbers. No account digits. Just proof the ladder works.
                    </p>
                    <Link
                        to="/wins"
                        data-testid="landing-wins-cta"
                        className="mt-5 inline-flex items-center gap-2 h-12 px-5 rounded-[12px] bg-white text-[var(--speakup-text-strong)] font-semibold"
                    >
                        See the Wall of Wins <ArrowRight size={16} />
                    </Link>
                </div>
                <div className="md:col-span-5 grid grid-cols-2 gap-4">
                    <DarkStat label="Wins published" value={stats?.total_wins ?? "—"} />
                    <DarkStat
                        label="Total recovered"
                        value={stats ? fmtMoney(stats.total_amount_recovered) : "—"}
                    />
                    <DarkStat label="Median days to resolve" value="< 30" />
                    <DarkStat label="Stage of escalation needed" value="usually L1–L2" />
                </div>
            </div>
        </section>
    );
}

function DarkStat({ label, value }) {
    return (
        <div className="rounded-[12px] p-4 bg-white/5 border border-white/10">
            <div className="text-[10.5px] uppercase tracking-[0.08em] text-white/55">{label}</div>
            <div className="display text-[22px] mt-1 tabular" style={{ color: "#fff" }}>
                {value}
            </div>
        </div>
    );
}

/* ---------------- Pricing ---------------- */
function Pricing() {
    return (
        <section className="max-w-3xl mx-auto px-5 md:px-8 pt-20 md:pt-28 text-center">
            <SectionLabel>Pricing</SectionLabel>
            <h2 className="display text-[34px] md:text-[48px] mt-3 leading-[1.05]">
                Free. Always. <span style={{ color: "var(--speakup-red)" }}>Period.</span>
            </h2>
            <p className="text-[var(--speakup-text-muted)] mt-4 text-[16px] max-w-xl mx-auto">
                We will never charge consumers to file a grievance against an institution that already has their
                money. If SpeakUp ever monetises, it will be on the institution side — never yours.
            </p>
            <div className="mt-7 inline-flex items-center gap-3 chip chip-resolved" data-testid="landing-pricing-chip">
                <Check size={14} /> No card. No subscription. No catch.
            </div>
        </section>
    );
}

/* ---------------- FAQ ---------------- */
function Faq() {
    const items = [
        {
            q: "Is SpeakUp legal advice?",
            a: "No. SpeakUp helps you draft a factual grievance and points you at the right officer on the RBI ladder. The facts you attest to are yours; we never invent details.",
        },
        {
            q: "Do you send the email to my bank for me?",
            a: "Never. SpeakUp opens your Gmail or default mail app with everything pre-filled — recipient, subject, body. You hit send. A hidden BCC comes back to us so we know it went out.",
        },
        {
            q: "Which institutions are supported right now?",
            a: "Today: HDFC Bank, ICICI Bank, Axis Bank and Bajaj Finance — each wired all the way through L1, Nodal Officer and Principal Nodal Officer. We're adding more every week. Don't see yours? Tap 'Request we add it' after sign-up.",
        },
        {
            q: "When can I file with the RBI Ombudsman?",
            a: "Under RB-IOS 2021, you can file once (a) the institution has had at least 30 days to respond from your first complaint AND you have a reference/acknowledgement number on file, OR (b) they explicitly rejected your complaint. SpeakUp tracks both clocks for you and unlocks the co-pilot when you qualify.",
        },
        {
            q: "What languages do you support for voice intake?",
            a: "We use Sarvam Saaras v3, which auto-detects Hindi, English, Tamil, Telugu, Bengali, Marathi, Gujarati, Kannada, Malayalam, Punjabi and more. You speak; we generate a working English transcript while showing you the native-script transcript to confirm.",
        },
        {
            q: "What happens to my data?",
            a: "Voice clips auto-expire after 30 days. The rest stays as long as your complaint is live. You can wipe everything — complaints, voice, account — from Account → Delete my data. Anonymous Wall-of-Wins entries are deleted along with you.",
        },
        {
            q: "How does the BCC tracking work?",
            a: "Each complaint gets a unique alias like c-<id>@reply.speakup.in. We add it as a BCC when you send. Any reply you receive that you forward to that alias auto-classifies and updates your timeline. (Today you can also paste the reply manually inside SpeakUp — same result.)",
        },
        {
            q: "Is this an app or a website?",
            a: "Both — SpeakUp is a Progressive Web App. Open it once in Chrome or Safari, tap 'Install' (or Add to Home Screen on iOS), and it lives on your home screen like any app.",
        },
    ];
    return (
        <section id="faq" className="max-w-3xl mx-auto px-5 md:px-8 pt-20 md:pt-28" data-testid="landing-faq">
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="display text-[34px] md:text-[48px] mt-3 leading-[1.05]">Questions, answered honestly.</h2>
            <div className="mt-10 divide-y divide-[var(--speakup-border)] border-t border-b border-[var(--speakup-border)]">
                {items.map((it, i) => (
                    <FaqItem key={it.q} q={it.q} a={it.a} idx={i} />
                ))}
            </div>
        </section>
    );
}

function FaqItem({ q, a, idx }) {
    const [open, setOpen] = useState(idx === 0);
    return (
        <div data-testid={`faq-item-${idx}`}>
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between gap-4 py-5 text-left"
                data-testid={`faq-toggle-${idx}`}
                aria-expanded={open}
            >
                <span className="display text-[17px] md:text-[18px] text-[var(--speakup-text-strong)] leading-snug">
                    {q}
                </span>
                <ChevronDown
                    size={18}
                    className={`shrink-0 transition-transform text-[var(--speakup-text-muted)] ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div className="pb-5 text-[15px] text-[var(--speakup-text-muted)] leading-relaxed -mt-1.5">
                    {a}
                </div>
            )}
        </div>
    );
}

/* ---------------- Final CTA ---------------- */
function FinalCta() {
    return (
        <section className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-12">
            <div
                className="rounded-[20px] p-8 md:p-14 text-center relative overflow-hidden"
                style={{
                    background: "var(--speakup-gradient)",
                    color: "#fff",
                }}
                data-testid="landing-final-cta"
            >
                <div className="display text-[36px] md:text-[56px] leading-[1.02]" style={{ color: "#fff" }}>
                    Got a banking issue right now?
                </div>
                <p className="mt-4 text-white/85 max-w-2xl mx-auto text-[16px] md:text-[17px]">
                    Stop arguing with the customer-care script. In two minutes, you'll have a formal complaint that lands
                    on the right desk — and a plan for what happens if they don't reply.
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                    <Link
                        to="/login"
                        data-testid="landing-final-primary-cta"
                        className="h-14 px-7 inline-flex items-center gap-2 rounded-[14px] bg-white text-[var(--speakup-text-strong)] font-semibold"
                    >
                        <Mic size={18} /> Raise your complaint
                    </Link>
                    <Link
                        to="/wins"
                        className="h-14 px-6 inline-flex items-center gap-2 rounded-[14px] border border-white/30 text-white font-semibold"
                    >
                        <Trophy size={16} /> See wins first
                    </Link>
                </div>
                <div className="mt-6 text-[13px] text-white/75 inline-flex items-center gap-2">
                    <Phone size={13} /> Sign in with your phone — no password, no app store.
                </div>
            </div>
        </section>
    );
}

/* ---------------- Footer ---------------- */
function LandingFooter() {
    return (
        <footer className="border-t border-[var(--speakup-border)] mt-6">
            <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 grid md:grid-cols-3 gap-8 text-[13px] text-[var(--speakup-text-muted)]">
                <div>
                    <Link to="/" className="flex items-center gap-2.5">
                        <SpeakUpLogo size={26} />
                        <span className="display text-[18px]" style={{ color: "var(--speakup-text-strong)" }}>
                            SpeakUp
                        </span>
                    </Link>
                    <p className="mt-3 max-w-xs">
                        Voice-first grievance escalation for Indian banking, NBFC and credit-card consumers. Free, forever.
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <div className="text-[var(--speakup-text-strong)] font-semibold mb-2 text-[13px]">Product</div>
                        <ul className="space-y-1.5">
                            <li><a href="#how" className="hover:text-black">How it works</a></li>
                            <li><a href="#features" className="hover:text-black">Features</a></li>
                            <li><a href="#institutions" className="hover:text-black">Supported banks</a></li>
                            <li><Link to="/wins" className="hover:text-black">Wall of Wins</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-[var(--speakup-text-strong)] font-semibold mb-2 text-[13px]">Account</div>
                        <ul className="space-y-1.5">
                            <li><Link to="/login" className="hover:text-black">Log in</Link></li>
                            <li><Link to="/login" className="hover:text-black">Raise a complaint</Link></li>
                            <li><a href="#faq" className="hover:text-black">FAQ</a></li>
                        </ul>
                    </div>
                </div>
                <div className="md:text-right">
                    <div className="text-[var(--speakup-text-strong)] font-semibold mb-2 text-[13px]">Made in India</div>
                    <p className="max-w-xs md:ml-auto">
                        SpeakUp is not affiliated with any bank, NBFC, the RBI or any regulator. The facts you attest to are
                        yours. We draft and route — we never send email to institutions on your behalf.
                    </p>
                    <p className="mt-4 tabular">© {new Date().getFullYear()} SpeakUp.</p>
                </div>
            </div>
        </footer>
    );
}

function SectionLabel({ children }) {
    return (
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-[var(--speakup-red)] font-semibold">
            <span className="h-px w-6 bg-[var(--speakup-red)]/40" /> {children}
        </div>
    );
}
